"use client";

import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  HStack,
  Input,
  Text,
  Box,
  IconButton,
  Flex,
  useOutsideClick,
  useToast,
} from "@chakra-ui/react";
import { Plus, Trash2, X } from "lucide-react";

interface DynamicVariable {
  key: string;
  value: string;
}

interface DynamicVariablesButtonProps {
  onVariablesChange: (variables: Record<string, string>) => void;
  prompt?: string;
  beginMessage?: string;
}

export const DynamicVariablesButton: React.FC<DynamicVariablesButtonProps> = ({
  onVariablesChange,
  prompt = "",
  beginMessage = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [savedVariables, setSavedVariables] = useState<DynamicVariable[]>([]);
  const [stagedVariables, setStagedVariables] = useState<DynamicVariable[]>([]);
  const [newFields, setNewFields] = useState<DynamicVariable[]>([]);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const previousVariablesRef = useRef<Record<string, string>>({});
  const toast = useToast();

  useOutsideClick({
    ref: popoverContentRef,
    handler: () => setIsOpen(false),
  });

  // Function to extract variables from text
  const extractVariables = useCallback((text: string): string[] => {
    const regex = /{{([^}]+)}}/g;
    const matches = text.match(regex);
    if (!matches) return [];
    return matches.map((match) => match.slice(2, -2).trim());
  }, []);

  // Initialize variables from prompt and begin message
  useEffect(() => {
    const promptVars = extractVariables(prompt);
    const beginMessageVars = extractVariables(beginMessage);
    const uniqueVars = Array.from(
      new Set([...promptVars, ...beginMessageVars])
    );

    if (uniqueVars.length > 0) {
      const newVars = uniqueVars.map((key) => ({
        key,
        value: "",
      }));
      setSavedVariables(newVars);

      // When opening the popover, initialize staged variables with saved variables
      if (isOpen) {
        setStagedVariables([...newVars]);
      }
    }
  }, [prompt, beginMessage, extractVariables]);

  // When opening the popover, initialize staged variables with saved variables
  useEffect(() => {
    if (isOpen) {
      setStagedVariables([...savedVariables]);
      setNewFields([]);
    }
  }, [isOpen, savedVariables]);

  // Convert variables array to object format and update parent
  const updateParent = useCallback(
    (vars: DynamicVariable[]) => {
      const variablesObject = vars.reduce((acc, { key, value }) => {
        if (key.trim()) {
          acc[key.trim()] = value;
        }
        return acc;
      }, {} as Record<string, string>);

      // Only update if different from previous
      const prevKeys = Object.keys(previousVariablesRef.current);
      const currentKeys = Object.keys(variablesObject);

      if (prevKeys.length !== currentKeys.length) {
        previousVariablesRef.current = variablesObject;
        onVariablesChange(variablesObject);
        return;
      }

      let hasChanged = false;
      for (const key of currentKeys) {
        if (
          previousVariablesRef.current[key] !== variablesObject[key] ||
          !previousVariablesRef.current.hasOwnProperty(key)
        ) {
          hasChanged = true;
          break;
        }
      }

      if (hasChanged) {
        previousVariablesRef.current = variablesObject;
        onVariablesChange(variablesObject);
      }
    },
    [onVariablesChange]
  );

  // Update parent when saved variables change
  useEffect(() => {
    updateParent(savedVariables);
  }, [savedVariables, updateParent]);

  const handleAddNewField = () => {
    setNewFields([...newFields, { key: "", value: "" }]);
  };

  const handleRemoveNewField = (index: number) => {
    const updatedFields = [...newFields];
    updatedFields.splice(index, 1);
    setNewFields(updatedFields);
  };

  const handleRemoveVariable = (index: number) => {
    const updatedVariables = [...stagedVariables];
    updatedVariables.splice(index, 1);
    setStagedVariables(updatedVariables);
  };

  const handleSave = () => {
    // Validate new fields
    const validNewFields = newFields.filter((field) => field.key.trim() !== "");

    // Check for duplicate keys
    const allKeys = new Set(stagedVariables.map((v) => v.key));
    let hasDuplicates = false;

    for (const field of validNewFields) {
      if (allKeys.has(field.key)) {
        hasDuplicates = true;
        toast({
          title: `Duplicate variable name: ${field.key}`,
          status: "error",
          duration: 2000,
        });
        break;
      }
      allKeys.add(field.key);
    }

    if (hasDuplicates) return;

    // Combine staged variables with valid new fields
    const combinedVariables = [...stagedVariables, ...validNewFields];
    setSavedVariables(combinedVariables);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => {}}
      placement="bottom-end"
      closeOnBlur={false}
      autoFocus={false}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
          fontFamily="monospace"
          fontWeight="normal"
          px={2}
          minW="auto"
          color="Black"
        >
          {"{}"}
          {savedVariables.length > 0 && (
            <Box
              as="span"
              ml={1}
              bg="gray.200"
              px={1.5}
              py={0.5}
              borderRadius="full"
              fontSize="xs"
            >
              {savedVariables.length}
            </Box>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverContentRef}
        boxShadow="lg"
        width="550px"
        maxWidth="95vw"
      >
        <PopoverBody p={4}>
          <VStack spacing={4} align="stretch">
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="bold" fontSize="lg">
                Dynamic Variables
              </Text>
              <IconButton
                aria-label="Close"
                icon={<X size={16} />}
                size="sm"
                variant="ghost"
                onClick={handleCancel}
              />
            </Flex>

            <Text fontSize="sm" opacity={0.7}>
              Set dynamic variables for dashboard audio and llm tests
            </Text>

            <Box bg="gray.50" p={3} borderRadius="md">
              <Flex mb={2} fontWeight="medium" fontSize="sm">
                <Box flex="1">Variable Name</Box>
                <Box flex="1">Test Value</Box>
                <Box width="40px"></Box>
              </Flex>

              {/* Existing variables */}
              {stagedVariables.map((variable, index) => (
                <HStack key={`staged-${index}`} mb={2}>
                  <Input
                    value={variable.key}
                    isReadOnly
                    placeholder="Variable name"
                    size="md"
                  />
                  <Input
                    value={variable.value}
                    onChange={(e) => {
                      const updatedVariables = [...stagedVariables];
                      updatedVariables[index].value = e.target.value;
                      setStagedVariables(updatedVariables);
                    }}
                    placeholder="Enter the value"
                    size="md"
                  />
                  <IconButton
                    aria-label="Remove variable"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveVariable(index)}
                    opacity={0.6}
                    _hover={{ opacity: 1, color: "red.500" }}
                    color="gray.700"
                  />
                </HStack>
              ))}

              {/* New fields */}
              {newFields.map((field, index) => (
                <HStack key={`new-${index}`} mb={2}>
                  <Input
                    value={field.key}
                    onChange={(e) => {
                      const updatedFields = [...newFields];
                      updatedFields[index].key = e.target.value;
                      setNewFields(updatedFields);
                    }}
                    placeholder="Enter the variable name"
                    size="md"
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => {
                      const updatedFields = [...newFields];
                      updatedFields[index].value = e.target.value;
                      setNewFields(updatedFields);
                    }}
                    placeholder="Enter the value"
                    size="md"
                  />
                  <IconButton
                    aria-label="Remove field"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveNewField(index)}
                    _hover={{ color: "red.500" }}
                    color="gray.700"
                  />
                </HStack>
              ))}

              <Button
                leftIcon={<Plus size={16} />}
                size="sm"
                variant="outline"
                onClick={handleAddNewField}
                mt={2}
                colorScheme="gray"
              >
                Add
              </Button>
            </Box>

            <Flex justifyContent="flex-end" gap={2} mt={2}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                color="black"
                _hover={{ bg: "gray.100" }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} colorScheme="gray">
                Save
              </Button>
            </Flex>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
